import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { createCard, updateCard } from "../api/cards";

function parseEmployeeNotes(notesRaw) {
  const raw = notesRaw == null ? "" : String(notesRaw);
  const trimmed = raw.trim();
  if (!trimmed) return { age: "", gender: "", registry: "" };

  try {
    const obj = JSON.parse(trimmed);
    if (obj && typeof obj === "object" && obj._employee && typeof obj._employee === "object") {
      const age = obj._employee.age == null ? "" : String(obj._employee.age);
      const gender = obj._employee.gender == null ? "" : String(obj._employee.gender);
      const registry = obj._employee.registry == null ? "" : String(obj._employee.registry);
      const surname = obj._employee.surname == null ? "" : String(obj._employee.surname);
      const job = obj._employee.job == null ? "" : String(obj._employee.job);
      const freeNote = obj._employee.note == null ? "" : String(obj._employee.note);
      return { age, gender, registry, surname, job, freeNote };
    }
  } catch {
    // ignore
  }

  // Backward-compatible: treat existing notes as free note
  return { age: "", gender: "", registry: "", surname: "", job: "", freeNote: raw };
}

function buildEmployeeNotes({ age, gender, registry, surname, job, freeNote }) {
  const ageNum = age === "" || age == null ? null : Number(age);
  const safeAge = Number.isFinite(ageNum) ? ageNum : null;
  const safeGender = String(gender || "").trim() || null;
  const safeRegistry = String(registry || "").trim() || null;
  const safeSurname = String(surname || "").trim() || null;
  const safeJob = String(job || "").trim() || null;
  const safeNote = String(freeNote || "").trim() || null;

  if (!safeAge && !safeGender && !safeRegistry && !safeSurname && !safeJob && !safeNote) return null;
  return JSON.stringify({
    _employee: {
      age: safeAge,
      gender: safeGender,
      registry: safeRegistry,
      surname: safeSurname,
      job: safeJob,
      note: safeNote,
    },
  });
}

function CardModal({ type, data, onClose, onSaved }) {
  const [code, setCode] = useState("");
  const [customerType, setCustomerType] = useState(""); // '01' dis, '02' ic
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [employeeAge, setEmployeeAge] = useState("");
  const [employeeGender, setEmployeeGender] = useState("");
  const [employeeRegistry, setEmployeeRegistry] = useState("");
  const [employeeSurname, setEmployeeSurname] = useState("");
  const [employeeJob, setEmployeeJob] = useState("");
  const [employeeFreeNote, setEmployeeFreeNote] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEmployee = String(type || "").toLowerCase() === "employee";
  const isCustomer = String(type || "").toLowerCase() === "customer";

  useEffect(() => {
    if (data) {
      setCode(data.code || "");
      setCustomerType(data.customer_type || "");
      setName(data.name || "");
      setEmail(data.email || "");
      setPhone(data.phone || "");
      setAddress(data.address || "");
      setNotes(data.notes || "");

      const emp = parseEmployeeNotes(data.notes);
      setEmployeeAge(emp.age);
      setEmployeeGender(emp.gender);
      setEmployeeRegistry(emp.registry);
      setEmployeeSurname(emp.surname || "");
      setEmployeeJob(emp.job || "");
      setEmployeeFreeNote(emp.freeNote || "");
    } else {
      setCode("");
      setCustomerType("");
      setName("");
      setEmail("");
      setPhone("");
      setAddress("");
      setNotes("");
      setEmployeeAge("");
      setEmployeeGender("");
      setEmployeeRegistry("");
      setEmployeeSurname("");
      setEmployeeJob("");
      setEmployeeFreeNote("");
    }
  }, [data]);

  const typeLabel = useMemo(() => {
    const t = String(type || "").toLowerCase();
    if (t === "customer") return "Müşteri";
    if (t === "supplier") return "Tedarikçi";
    if (t === "employee") return "Çalışan";
    return "Kayıt";
  }, [type]);

  async function save() {
    setError("");
    const trimmedName = String(name || "").trim();
    if (!trimmedName) {
      setError("İsim zorunlu.");
      return;
    }

    const safeCode = String(code || "").trim() || null;
    const safeCustomerType = isCustomer ? (String(customerType || "").trim() || null) : null;

    const employeeNotes2 = isEmployee
      ? buildEmployeeNotes({
          age: employeeAge,
          gender: employeeGender,
          registry: employeeRegistry,
          surname: employeeSurname,
          job: employeeJob,
          freeNote: employeeFreeNote,
        })
      : null;

    const payload = {
      ...(data ? {} : { type: String(type || "").toLowerCase() }),
      code: safeCode,
      customer_type: safeCustomerType,
      market_code: safeCustomerType, // backward compatible
      name: trimmedName,
      email: String(email || "").trim() || null,
      phone: String(phone || "").trim() || null,
      address: isEmployee ? null : String(address || "").trim() || null,
      notes: isEmployee ? employeeNotes2 : String(notes || "").trim() || null,
    };

    setSaving(true);
    try {
      if (data?.id) await updateCard(data.id, payload);
      else await createCard(payload);

      await onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("CARD SAVE ERROR", err);
      const status = err?.response?.status;
      const dataObj = err?.response?.data;
      const message =
        dataObj?.error?.message ||
        dataObj?.message ||
        (typeof dataObj === "string" ? dataObj : null) ||
        err?.message ||
        null;
      setError(`Kaydetme başarısız${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {data ? `${typeLabel} Düzenle` : `Yeni ${typeLabel}`}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {isEmployee
            ? "Genel, iletişim ve personel bilgilerini düzenleyin."
            : "Genel, iletişim ve adres bilgilerini düzenleyin."}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Genel
            </Typography>
            <Grid container spacing={2}>
              {!isEmployee ? (
                <Grid item xs={12} md={3}>
                  <TextField
                    label={isCustomer ? "Müşteri Kodu" : `${typeLabel} Kodu`}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    disabled={saving}
                    fullWidth
                    helperText={isCustomer ? "Excel müşteri kodu" : "Opsiyonel"}
                  />
                </Grid>
              ) : null}

              {isCustomer ? (
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label="İç/Dış Piyasa"
                    value={customerType}
                    onChange={(e) => setCustomerType(e.target.value)}
                    disabled={saving}
                    fullWidth
                    helperText="Dış=01, İç=02 (ileride filtre için)"
                  >
                    <MenuItem value="">
                      <em>Seçilmedi</em>
                    </MenuItem>
                    <MenuItem value="01">Dış Piyasa (01)</MenuItem>
                    <MenuItem value="02">İç Piyasa (02)</MenuItem>
                  </TextField>
                </Grid>
              ) : null}

              <Grid item xs={12} md={6}>
                <TextField
                  label="Ad"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                  disabled={saving}
                  fullWidth
                />
              </Grid>
              {isEmployee ? (
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Soyad"
                    value={employeeSurname}
                    onChange={(e) => setEmployeeSurname(e.target.value)}
                    disabled={saving}
                    fullWidth
                  />
                </Grid>
              ) : (
              <Grid item xs={12} md={6}>
                <TextField label="Durum" value="Aktif" disabled fullWidth helperText="(Şu an sadece aktif kayıtlar listelenir)" />
              </Grid>
              )}
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              İletişim
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Telefon"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={saving}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="E-posta"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={saving}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {isEmployee ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Personel Bilgileri
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Yaş"
                    type="number"
                    value={employeeAge}
                    onChange={(e) => setEmployeeAge(e.target.value)}
                    disabled={saving}
                    fullWidth
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label="Cinsiyet"
                    value={employeeGender}
                    onChange={(e) => setEmployeeGender(e.target.value)}
                    disabled={saving}
                    fullWidth
                  >
                    <MenuItem value="">
                      <em>Seçilmedi</em>
                    </MenuItem>
                    <MenuItem value="erkek">Erkek</MenuItem>
                    <MenuItem value="kadın">Kadın</MenuItem>
                    <MenuItem value="diger">Diğer</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Görev"
                    value={employeeJob}
                    onChange={(e) => setEmployeeJob(e.target.value)}
                    disabled={saving}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Kütük Bilgisi"
                    value={employeeRegistry}
                    onChange={(e) => setEmployeeRegistry(e.target.value)}
                    disabled={saving}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Adres
              </Typography>
              <TextField
                label="Adres"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                disabled={saving}
                fullWidth
                multiline
                minRows={2}
              />
            </Box>
          )}

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Notlar
            </Typography>
            <TextField
              label="Not"
              value={isEmployee ? employeeFreeNote : notes}
              onChange={(e) => (isEmployee ? setEmployeeFreeNote(e.target.value) : setNotes(e.target.value))}
              disabled={saving}
              fullWidth
              multiline
              minRows={3}
              placeholder={isEmployee ? "Çalışan için not..." : undefined}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          İptal
        </Button>
        <Button onClick={save} variant="contained" disabled={saving}>
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CardModal;
